import {
  getInternalLedgerAdjustmentControllerFindOneQueryKey,
  getInternalLedgerControllerFindOneQueryKey,
  getInternalLedgerSessionControllerFindOneActiveQueryKey,
  getInternalLedgerSessionControllerFindOneQueryKey,
  InternalLedgerSessionStatus,
  useInternalLedgerControllerFindOne,
  useInternalLedgerSessionControllerClose,
  useInternalLedgerSessionControllerCount,
  useInternalLedgerSessionControllerEnd,
  useInternalLedgerSessionControllerFindOne,
  useInternalLedgerSessionControllerFindOneActive,
  useInternalLedgerSessionControllerOpen,
  useInternalLedgerSessionControllerStart,
} from '@konsolenkost-de/acquisition_store_server_sdk_vue';
import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { usePiniaRefStore } from '../composables/usePiniaRefStore';
import { useResponseData } from '../composables/useResponseData';
import { useLoading } from './loadingStore';
import { useSource } from './sourceStore';

export const useLedgerSessionStore = defineStore(
  'ledger-session',
  () => {
    const { currentSource } = useSource();

    const lastAdjustmentTimestamp = ref<number>(0);
    const ledgerId = ref<number>(0);
    const ledgerSessionId = ref<number>(0);
    const countedAmount = ref<number>(0);

    const hasLedger = computed(() => ledgerId.value > 0);
    const hasLedgerSession = computed(() => ledgerId.value > 0 && ledgerSessionId.value > 0);

    const {
      data: ledgerData,
      isFetching: isLedgerLoading,
      refetch: refetchLedger,
    } = useInternalLedgerControllerFindOne(ledgerId, {
      query: {
        enabled: hasLedger,
        queryKey: getInternalLedgerControllerFindOneQueryKey(ledgerId),
        refetchOnMount: true,
      },
    });
    const {
      data: activeLedgerSessionData,
      refetch: refetchActiveLedgerSession,
      isFetching: isActiveLedgerSessionLoading,
    } = useInternalLedgerSessionControllerFindOneActive(ledgerId, {
      query: {
        enabled: hasLedger,
        queryKey: getInternalLedgerSessionControllerFindOneActiveQueryKey(ledgerId),
        refetchOnMount: true,
      },
    });
    const {
      data: ledgerSessionData,
      isFetching: isLedgerSessionLoading,
      refetch: refetchLedgerSession,
    } = useInternalLedgerSessionControllerFindOne(ledgerId, ledgerSessionId, {
      query: {
        enabled: hasLedgerSession,
        queryKey: getInternalLedgerSessionControllerFindOneQueryKey(
          ledgerId.value,
          ledgerSessionId.value,
        ),
        refetchOnMount: true,
      },
    });

    const { mutateAsync: openLedgerSession, isPending: isOpenLedgerSessionPending } =
      useInternalLedgerSessionControllerOpen({
        mutation: {
          onSuccess: ({ data, status }) => {
            if (status === 201) ledgerSessionId.value = data.id;
          },
        },
      });
    const { mutateAsync: startLedgerSession, isPending: isStartLedgerSessionPending } =
      useInternalLedgerSessionControllerStart({
        mutation: { onSuccess: () => refetchLedgerSession() },
      });
    const { mutateAsync: countLedgerSession, isPending: isCountLedgerSessionPending } =
      useInternalLedgerSessionControllerCount({
        mutation: { onSuccess: () => refetchLedgerSession() },
      });
    const { mutateAsync: endLedgerSession, isPending: isEndLedgerSessionPending } =
      useInternalLedgerSessionControllerEnd({
        mutation: { onSuccess: () => refetchLedgerSession() },
      });
    const { mutateAsync: closeLedgerSession, isPending: isCloseLedgerSessionPending } =
      useInternalLedgerSessionControllerClose({
        mutation: { onSuccess: () => refetchLedgerSession() },
      });

    const ledger = useResponseData(ledgerData);
    const activeLedgerSession = useResponseData(activeLedgerSessionData);
    const ledgerSession = useResponseData(ledgerSessionData);

    const expectedAmount = computed(() =>
      ledgerSession.value?.status === InternalLedgerSessionStatus.COUNTING_OPEN
        ? ledgerSession.value.expectedOpeningFloat
        : ledgerSession.value?.expectedClosingFloat,
    );

    const actualAmount = computed(() =>
      ledgerSession.value?.status === InternalLedgerSessionStatus.COUNTING_OPEN
        ? ledgerSession.value.actualOpeningFloat
        : (ledgerSession.value?.actualClosingFloat ?? 0),
    );

    const discrepancy = computed(() =>
      ledgerSession.value?.status === InternalLedgerSessionStatus.COUNTING_OPEN
        ? ledgerSession.value.openingFloatDiscrepancy
        : (ledgerSession.value?.closingFloatDiscrepancy ?? 0),
    );

    const countAttempts = computed(() =>
      ledgerSession.value?.status === InternalLedgerSessionStatus.COUNTING_OPEN
        ? ledgerSession.value.openingCountAttempts
        : (ledgerSession.value?.closingCountAttempts ?? 0),
    );

    watch(
      ledgerSession,
      (session) => {
        if (session?.status === InternalLedgerSessionStatus.OPEN) {
          countedAmount.value = 0;
        } else if (session?.status === InternalLedgerSessionStatus.CLOSED) {
          // Invalidate ledger session when it is closed to prevent usage of stale data and potential errors
          ledgerSessionId.value = 0;
          countedAmount.value = 0;
        }
      },
      { immediate: true },
    );

    watch([currentSource, ledger], ([source, ledgerValue]) => {
      if (source && ledgerValue && source.id !== ledgerValue?.source.id) {
        ledgerId.value = 0;
        ledgerSessionId.value = 0;
      }
    });

    watch(lastAdjustmentTimestamp, () => refetchLedgerSession());

    return {
      ledgerId,
      ledgerSessionId,
      hasLedger,
      hasLedgerSession,
      ledger,
      activeLedgerSession,
      ledgerSession,
      countedAmount,
      isLedgerLoading,
      isLedgerSessionLoading,
      isOpenLedgerSessionPending,
      isStartLedgerSessionPending,
      isCountLedgerSessionPending,
      isEndLedgerSessionPending,
      isCloseLedgerSessionPending,
      isActiveLedgerSessionLoading,
      lastAdjustmentTimestamp,
      expectedAmount,
      actualAmount,
      discrepancy,
      countAttempts,
      openLedgerSession,
      startLedgerSession,
      countLedgerSession,
      endLedgerSession,
      closeLedgerSession,
      refetchLedger,
      refetchActiveLedgerSession,
      refetchLedgerSession,
    };
  },
  {
    persist: {
      pick: ['ledgerId', 'ledgerSessionId'],
    },
  },
);

export const useLedgerSession = () => {
  const store = usePiniaRefStore(useLedgerSessionStore());
  const { watchLoading } = useLoading();

  watchLoading(
    store.isLedgerLoading,
    store.isLedgerSessionLoading,
    store.isActiveLedgerSessionLoading,
    store.isOpenLedgerSessionPending,
    store.isStartLedgerSessionPending,
    store.isCountLedgerSessionPending,
    store.isEndLedgerSessionPending,
    store.isCloseLedgerSessionPending,
  );

  return store;
};
